<template>
<div data-component="PromoMessages.vue" class="promo-messages">
	<PageHeader Title="Powiadomienia" :Back="true">
		<DropdownSelect
			class="!absolute right-4 top-6"
			:Items="notificationsMenu"
			:Arrowless="true"
			@change="goMenuItem"
		>
			<template #label>
				<OIcon name="more" size="1.25"/>
			</template>
		</DropdownSelect>
	</PageHeader>

	<div class="m-4 lg:m-6 lg:px-1 overflow-auto lg:max-h-[560px]">
		<template v-if="messages.length">
			<div v-for="message in messages" :key="message.id" class="flex p-2 mb-2 rounded-lg shadow-md cursor-pointer" @click="onMessageClick(message)">
				<OIcon :size="1" class="shrink-0 mr-2 relative top-0.5" v-bind="getMessageIcon(message)"/>
				<div class="w-full">
					<div class="flex justify-between text-sm" :class="{ 'font-bold': !message.isRead }">
						<div v-html="message.title"/>
						<div v-if="ScreenSize > 1" class="ml-2 text-xs text-neutral-600 shrink-0" v-html="getMessageDate(message)"/>
					</div>
					<div v-if="message.message" class="mt-1 text-xs text-neutral-600 lg:mt-2" v-html="message.message"/>
					<div v-if="ScreenSize <= 1" class="mt-1 text-xs text-neutral-600" v-html="getMessageDate(message)"/>
				</div>
			</div>
		</template>
		<div
			v-else
			class="py-4 text-base font-bold text-center text-neutral-600"
			v-html="'Nie masz żadnych powiadomień'"
		/>
	</div>
</div>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import ServiceMessage from '@globals/types/ServiceMessage';
import { usePromoMessagesStore } from '@customer/stores/promoMessages';
import jsonFetch from '@globals/jsonFetch';

/**
 * Komponent odpowiedzialny za wyświetlanie wiadomości marketingowych na górnym pasku nawigacyjnym oraz na stronie powiadomień
 */
export default {
	name: 'PromoMessages',
	data() {
		return {
			/** Czy jest wyłączony dźwięk powiadomień? */
			isAudioDisabled: window.localStorage.DisableAudio === 'true',
		};
	},
	computed: {
		...mapStores(usePromoMessagesStore),

		/** Lista wszystkich wiadomości marketingowych */
		messages() {
			return this.promoMessagesStore.promoMessagesList;
		},

		/** Pozycje menu dostępnego pod wielokropkiem */
		notificationsMenu() {
			return [
				{ Label: `${this.isAudioDisabled ? 'Włącz' : 'Wyłącz'} dźwięk powiadomień`, Value: 'toggleAudio' },
				{ Label: 'Ustawienia powiadomień', Value: 'settings' },
			];
		},
	},
	mounted() {
		this.onOpen();
	},
	methods: {
		/**
		 * Zwraca datę otrzymania wiadomości w postaci "Pon, 1 kwi 2023, 21:37"
		 */
		getMessageDate(message:ServiceMessage) {
			const { date } = message;
			if (!date) { return ''; }

			const dayOfWeek = ['Nie', 'Pon', 'Wto', 'Śr', 'Czw', 'Pią', 'Sob'];

			return `${dayOfWeek[date.getDay()]}, ${date.toLocaleString('pl-PL', {
				month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric',
			})}`;
		},

		/**
		 * Zwraca kolor i nazwę ikony wiadomości
		 */
		getMessageIcon(message:ServiceMessage) {
			switch (message.type) {
			case 'success':
				return {
					name: 'notification-success',
					class: 'text-success-400',
				};
			case 'danger':
				return {
					name: 'notification-danger',
					class: 'text-danger',
				};
			case 'alert':
				return {
					name: 'notification-alert',
					class: 'text-warn',
				};
			case 'info':
			default:
				return {
					name: 'notification-info',
					class: 'text-info',
				};
			}
		},

		/**
		 * Podczas otworzenia panelu z powiadomieniami, odśwież listę, a następnie
		 * oznacz wszystkie wiadomości jako przeczytane.
		 */
		async onOpen() {
			await this.promoMessagesStore.update();
			this.promoMessagesStore.readAll();
		},

		/**
		 * Po kliknięciu wiadomości, przejdź na odpowiednią stronę używając metody `Act`
		 * StateManager'a. Dodatkowo oznacz wiadomość jako przeczytaną.
		 */
		onMessageClick(message:ServiceMessage) {
			const { action } = message;
			this.promoMessagesStore.markAsRead(message);
			if (!action) { return; }

			if (action === 'GlobalDialog:UnusedCats') {
				window.setTimeout(() => { this.State.Append({ GlobalDialog: 'UnusedCats', MessageId: message.id }); }, 150); return;
			}
			if (action === 'GlobalDialog:TheBestSurveyDialog') {
				const panelButton = document.querySelector('.global-notifications button');
				if (panelButton) { panelButton.click(); }
				window.setTimeout(() => { this.State.Append({ GlobalDialog: 'TheBestSurveyDialog' }); }, 150); return;
			}

			if (action === 'GoToAutenti') {
				const panelButton = document.querySelector('.global-notifications button');
				if (panelButton) { panelButton.click(); }
				this.State.GoToAutenti(); return;
			}

			if (action === 'ChangeNotificationsToSingle') {
				const panelButton = document.querySelector('.global-notifications button');
				if (panelButton) { panelButton.click(); }
				jsonFetch('/myoferteo/konto-json/change-notifications-to-single.do');
				this.Toast.Show({
					Message: 'Powiadomienia zostały zmienione',
					Style: 'ok',
				});
				return;
			}

			this.State.Act(action, null, true);
		},

		/**
		 * Wykonaj akcję menu, które znajduje się pod wielokropkiem
		 */
		goMenuItem(menuItem:string) {
			switch (menuItem) {
			case 'settings': document.location.href = '/konto/ustawienia/powiadomienia'; break;
			case 'toggleAudio': this.toggleAudio(); break;
			default: break;
			}
		},

		/**
		 * Przełącza ustawienie dźwięku nowej wiadomości oraz wysyła informację analityczną
		 */
		toggleAudio() {
			this.isAudioDisabled = !this.isAudioDisabled;
			window.localStorage.DisableAudio = String(this.isAudioDisabled);
			this.Analytics.sendGAEvent(`promoMessages_sound_toggle_${!this.isAudioDisabled ? 'on' : 'off'}`);
		},
	},
};

</script>

<style lang="scss">
.promo-messages .dropdown {
	& > button::after {
		display: none;
	}

	.suggestions-list {
		top: 2rem;
	}
}
</style>
